<script setup lang="ts">
/* 전역 confirm / prompt dialog — Radix Vue Dialog 기반.
   App.vue 에 한 번 mount, 모든 페이지가 useConfirm() · usePrompt() 로 사용.
   store.input 이 null 이 아니면 입력 필드를 함께 렌더한다. */
import { computed, nextTick, ref, watch } from 'vue';
import {
  DialogRoot,
  DialogPortal,
  DialogOverlay,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from 'radix-vue';
import { useConfirmStore } from '@/stores/confirmDialog';
import AppButton from './AppButton.vue';
import { AlertTriangle, HelpCircle } from 'lucide-vue-next';

const store = useConfirmStore();
const field = ref<HTMLInputElement | HTMLTextAreaElement | null>(null);

const inputType = computed(() => store.options?.inputType ?? 'text');
const remaining = computed(() => {
  const max = store.options?.maxLength;
  return max && store.input !== null ? max - store.input.length : null;
});

// 입력 모드로 열리면 바로 타이핑할 수 있어야 한다.
watch(
  () => store.isOpen,
  (open) => {
    if (open && store.input !== null) void nextTick(() => field.value?.focus());
  },
);
</script>

<template>
  <DialogRoot v-model:open="store.isOpen">
    <DialogPortal>
      <DialogOverlay class="fixed inset-0 bg-[rgba(15,39,66,0.6)] z-40 data-[state=open]:animate-in data-[state=open]:fade-in" />
      <DialogContent
        class="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[90%] max-w-[480px] bg-canvas rounded-card shadow-modal z-50 overflow-hidden focus:outline-none"
        @escape-key-down="store.cancel"
        @pointer-down-outside="store.cancel"
      >
        <!-- Top accent for destructive -->
        <div
          v-if="store.options?.variant === 'destructive'"
          class="h-1 bg-critical"
        />

        <div class="p-6 flex items-start gap-4">
          <div
            class="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
            :class="store.options?.variant === 'destructive' ? 'bg-critical-soft' : 'bg-info-soft'"
          >
            <AlertTriangle
              v-if="store.options?.variant === 'destructive'"
              class="w-5 h-5 text-critical"
              :stroke-width="2"
            />
            <HelpCircle v-else class="w-5 h-5 text-info" :stroke-width="2" />
          </div>
          <div class="flex-1 min-w-0">
            <DialogTitle class="text-lg font-bold text-charcoal-headline">
              {{ store.options?.title }}
            </DialogTitle>
            <DialogDescription
              v-if="store.options?.message"
              class="mt-1.5 text-sm text-charcoal-body whitespace-pre-line"
            >
              {{ store.options.message }}
            </DialogDescription>

            <div v-if="store.input !== null" class="mt-4">
              <label
                for="confirm-dialog-input"
                class="mb-1 block text-xs font-semibold text-secondary-grey"
              >
                {{ store.options?.inputLabel }}
                <span v-if="!store.options?.required" class="font-normal">(optional)</span>
              </label>
              <textarea
                v-if="inputType === 'textarea'"
                id="confirm-dialog-input"
                ref="field"
                v-model="store.input"
                rows="3"
                :maxlength="store.options?.maxLength"
                :placeholder="store.options?.placeholder"
                class="w-full resize-y rounded-utility border border-hairline px-3 py-2 text-sm text-charcoal-body focus:border-kag-navy focus:outline-none"
              />
              <input
                v-else
                id="confirm-dialog-input"
                ref="field"
                v-model="store.input"
                :type="inputType === 'month' ? 'month' : inputType"
                :maxlength="store.options?.maxLength"
                :min="store.options?.min"
                :placeholder="store.options?.placeholder"
                class="h-10 w-full rounded-utility border border-hairline px-3 text-sm text-charcoal-body focus:border-kag-navy focus:outline-none"
                @keydown.enter.prevent="store.confirm"
              />
              <p class="mt-1 flex items-start justify-between gap-3 text-xs">
                <span :class="store.inputError ? 'text-critical font-semibold' : 'text-secondary-grey'">
                  {{ store.inputError ?? store.options?.hint }}
                </span>
                <span v-if="remaining !== null" class="shrink-0 tabular-nums text-secondary-grey">
                  {{ remaining }}
                </span>
              </p>
            </div>
          </div>
        </div>

        <footer class="border-t border-hairline px-6 py-4 flex items-center justify-end gap-2 bg-canvas">
          <AppButton variant="secondary" size="sm" @click="store.cancel">
            {{ store.options?.cancelLabel ?? 'Cancel' }}
          </AppButton>
          <!-- own-gate: 이 창은 이미 보이는 버튼을 눌러야 열린다 — 권한 판정은 그 버튼(과 서버)이 했다.
               경로 키로 다시 숨기면 탭 키로 열린 버튼(Commission Setting Confirm 의 Contract
               Approvals 등)에서 창만 뜨고 확인 버튼이 없어 Cancel 만 남았다. -->
          <AppButton
            :variant="store.options?.variant === 'destructive' ? 'primary' : 'authority'"
            size="sm"
            own-gate
            :disabled="!store.canConfirm"
            :class="store.options?.variant === 'destructive' ? 'bg-critical hover:!bg-critical/90 active:!bg-critical/80 border-transparent' : ''"
            @click="store.confirm"
          >
            {{ store.options?.confirmLabel ?? 'Confirm' }}
          </AppButton>
        </footer>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>
